import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { GlobovmsLogo } from "./brand/GlobovmsLogo";
import { Icon } from "./icons";
import { useSession } from "../context/Session";

const groups = [
  ["Overview", [["Dashboard", "/", "home"]]],
  ["Visitors", [["Visitors", "/visitors", "visits"], ["Expected", "/visitors?view=expected", "calendar"], ["Approvals", "/approvals", "audit"]]],
  ["People", [["Hosts", "/hosts", "staff"], ["Contractors", "/contractors", "clients"]]],
  ["Facilities", [["Sites", "/sites", "sites"], ["Kiosks", "/kiosks", "kiosks"]]],
  ["Safety", [["Emergency", "/emergency", "bell"]]],
  ["Administration", [["Reports", "/reports", "visits"], ["Audit", "/audit", "audit"], ["Settings", "/settings", "lock"]]],
];

function initials(profile) {
  const source = profile?.organization?.name || profile?.role?.name || "GV";
  const parts = source.split(/[\s.@]+/).filter(Boolean);
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
}

function itemActive(to, isActive) {
  const onExpected = window.location.search.includes("view=expected");
  if (to.includes("view=expected")) return onExpected;
  if (to === "/visitors") return isActive && !onExpected;
  return isActive;
}

function NavGroups({ onNavigate }) {
  return (
    <nav className="space-y-5 px-3 py-4" aria-label="Portal">
      {groups.map(([title, links]) => (
        <div key={title}>
          <p className="mb-1 px-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">{title}</p>
          <div className="grid">
            {links.map(([label, to, icon]) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                onClick={onNavigate}
                className={({ isActive }) => `group relative flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-semibold transition-colors duration-200 ${itemActive(to, isActive) ? "bg-white/10 text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"}`}
              >
                {({ isActive }) => {
                  const active = itemActive(to, isActive);
                  return (
                    <>
                      <span className={`absolute left-0 top-1/2 h-4 w-[3px] -translate-y-1/2 rounded-full bg-brand-blue transition-opacity duration-200 ${active ? "opacity-100" : "opacity-0"}`} />
                      <Icon name={icon} className={`h-[18px] w-[18px] shrink-0 ${active ? "text-brand-blue" : "text-slate-400 group-hover:text-slate-200"}`} />
                      <span className="min-w-0 flex-1 truncate">{label}</span>
                    </>
                  );
                }}
              </NavLink>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}

function BrandRow({ actionLabel, onAction, icon, buttonRef, expanded }) {
  return (
    <div className="flex h-16 shrink-0 items-center justify-between gap-2 border-b border-white/10 px-4">
      <GlobovmsLogo light />
      <button ref={buttonRef} type="button" className="rounded-lg border border-white/15 p-1.5 text-slate-300 transition-colors duration-200 hover:bg-white/10 hover:text-white" aria-label={actionLabel} aria-expanded={expanded} aria-controls={expanded === undefined ? undefined : "portal-sidebar"} onClick={onAction}>
        <Icon name={icon} className="h-4 w-4" />
      </button>
    </div>
  );
}

function SidebarFoot({ profile }) {
  return (
    <div className="border-t border-white/10 p-3">
      <div className="flex items-center gap-2.5 rounded-lg px-2 py-2">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/10 text-xs font-bold text-brand-blue">{initials(profile)}</span>
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold leading-4 text-white">{profile?.organization?.name || "Organization"}</span>
          <span className="mt-0.5 block truncate text-xs text-slate-400">{profile?.role?.name || "Member"}</span>
        </span>
      </div>
    </div>
  );
}

function readCollapsed() {
  try { return localStorage.getItem("globovms-app-sidebar") === "hidden"; }
  catch { return false; }
}

export default function Shell() {
  const { profile, signOut } = useSession();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [menu, setMenu] = useState(false);
  const menuRef = useRef(null);
  const hideRef = useRef(null);
  const showRef = useRef(null);
  const sidebarRef = useRef(null);
  const focusTarget = useRef(null);

  function hideSidebar() {
    focusTarget.current = "show";
    setCollapsed(true);
  }

  function showSidebar() {
    focusTarget.current = "hide";
    setCollapsed(false);
  }

  useEffect(() => {
    try { localStorage.setItem("globovms-app-sidebar", collapsed ? "hidden" : "shown"); }
    catch { /* storage can be unavailable */ }
    if (sidebarRef.current) {
      if (collapsed) sidebarRef.current.setAttribute("inert", "");
      else sidebarRef.current.removeAttribute("inert");
    }
    if (focusTarget.current === "show") showRef.current?.focus();
    if (focusTarget.current === "hide") hideRef.current?.focus();
    focusTarget.current = null;
  }, [collapsed]);

  useEffect(() => {
    function close(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) setMenu(false);
    }
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <div className="min-h-screen bg-[#f4f7fb] text-brand-ink">
      <div className="flex min-h-screen">
        <aside ref={sidebarRef} id="portal-sidebar" aria-hidden={collapsed} className={`sticky top-0 hidden h-screen shrink-0 self-start overflow-hidden border-r bg-[#0B1C33] transition-[width] duration-200 ease-out md:block ${collapsed ? "w-0 border-transparent" : "w-64 border-white/10"}`}>
          <div className="flex h-full w-64 flex-col">
            <BrandRow buttonRef={hideRef} expanded icon="sidebar" actionLabel="Hide sidebar" onAction={hideSidebar} />
            <div className="flex-1 overflow-y-auto">
              <NavGroups />
            </div>
            <SidebarFoot profile={profile} />
          </div>
        </aside>
        {open ? (
          <div className="fixed inset-0 z-40 md:hidden">
            <button className="absolute inset-0 bg-slate-900/40" aria-label="Close menu" onClick={() => setOpen(false)} />
            <aside className="relative flex h-full w-72 flex-col bg-[#0B1C33] shadow-lg">
              <BrandRow icon="close" actionLabel="Close menu" onAction={() => setOpen(false)} />
              <div className="flex-1 overflow-y-auto">
                <NavGroups onNavigate={() => setOpen(false)} />
              </div>
              <SidebarFoot profile={profile} />
            </aside>
          </div>
        ) : null}
        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-30 flex h-16 items-center justify-between gap-3 border-b border-slate-200/80 bg-white px-4 md:px-6">
            <div className="flex min-w-0 items-center gap-2">
              <button type="button" className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 md:hidden" aria-label="Open menu" onClick={() => setOpen(true)}><Icon name="menu" /></button>
              {collapsed ? <button ref={showRef} type="button" className="hidden rounded-lg p-2 text-slate-600 hover:bg-slate-100 md:inline-flex" aria-label="Show sidebar" aria-expanded="false" aria-controls="portal-sidebar" onClick={showSidebar}><Icon name="sidebar" className="h-5 w-5" /></button> : null}
              <p className="truncate text-sm text-slate-600">
                <span className="font-semibold text-brand-ink">{profile?.organization?.name || "Organization"}</span>
                <span className="hidden sm:inline"> · {profile?.role?.name || "Member"}</span>
              </p>
            </div>
            <div className="relative" ref={menuRef}>
              <button className="flex items-center gap-2 rounded-full py-1 pl-1 pr-2 hover:bg-slate-100" aria-expanded={menu} aria-haspopup="menu" onClick={() => setMenu((value) => !value)}>
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-sky-100 text-xs font-bold text-brand-blue">{initials(profile)}</span>
                <span className="hidden text-left sm:block">
                  <span className="block text-sm font-semibold leading-4">{profile?.organization?.name || "Organization"}</span>
                  <span className="block text-xs text-slate-500">{profile?.role?.name || "Member"}</span>
                </span>
              </button>
              {menu ? (
                <div role="menu" className="absolute right-0 mt-2 w-44 rounded-lg border border-slate-200 bg-white p-1 shadow-sm">
                  <button role="menuitem" className="w-full rounded-md px-3 py-2 text-left text-sm font-semibold text-slate-700 hover:bg-slate-50" onClick={signOut}>Sign out</button>
                </div>
              ) : null}
            </div>
          </header>
          <main className="p-4 md:p-6"><Outlet /></main>
        </div>
      </div>
    </div>
  );
}
